// Le lecteur embed4me — `eps1` chez anime-sama — ne publie rien dans sa page :
// c'est une application qui lit l'identifiant dans le fragment (`/#pv3nf`, que
// le navigateur n'envoie jamais au serveur) puis interroge son API.
//
// Les réponses de cette API sont chiffrées en AES-128-CBC. La clé et l'IV sont
// reconstruits par le bundle à partir de `location.protocol` uniquement : ils
// ne dépendent ni de la vidéo, ni de la session, ni de l'identifiant. Ils sont
// donc constants, et reproduits ici tels que la dérivation les produit.
//
// Ce module sert des deux côtés : le client y extrait l'URL du flux, et le
// serveur s'en sert pour découvrir l'hôte du CDN — sans quoi celui-ci, absent
// en clair de la réponse, serait refusé par son allowlist.

const KEY = "kiemtienmua911ca";
const IV = "1234567890oiuytr";

/** Réponse de `/api/v1/video`. Seuls les champs exploités sont décrits ; l'API
 *  en renvoie beaucoup d'autres (analytics, trackers WebRTC, thème du player). */
export interface Embed4meVideo {
  /** Master HLS servi par le CDN d'origine, sur une IP nue. */
  source?: unknown;
  /** Même flux relayé par le domaine du lecteur. Plus lent, mais il survit
   *  quand l'hôte du CDN n'a pas pu être découvert. */
  cfNative?: unknown;
  title?: unknown;
}

const HEX_RE = /^[0-9a-f]+$/i;

/** L'identifiant vit dans le fragment, et un `&` peut le suivre. */
export function videoId(embedUrl: string): string | null {
  let hash: string;

  try {
    hash = new URL(embedUrl).hash.slice(1);
  } catch {
    return null;
  }

  const id = hash.split("&")[0] ?? "";

  return id.length > 1 ? id : null;
}

export function apiUrl(embedUrl: string, id: string): string {
  return `${new URL(embedUrl).origin}/api/v1/video?id=${encodeURIComponent(id)}`;
}

/** Reconnaît une réponse à déchiffrer, côté serveur, à partir de l'URL amont. */
export function isApiUrl(url: string): boolean {
  try {
    const parsed = new URL(url);

    return (
      parsed.hostname.toLowerCase().endsWith("embed4me.com") &&
      parsed.pathname === "/api/v1/video"
    );
  } catch {
    return false;
  }
}

/** Clé et IV sont des chaînes ASCII : un encodage octet par octet suffit, et
 *  rend un `ArrayBuffer` que Web Crypto accepte des deux côtés. */
function asciiBuffer(text: string): ArrayBuffer {
  const buffer = new ArrayBuffer(text.length);
  const bytes = new Uint8Array(buffer);

  for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i);

  return buffer;
}

function hexToBuffer(hex: string): ArrayBuffer | null {
  const clean = hex.trim();

  // une erreur de l'API arrive en JSON clair, pas en hexadécimal
  if (clean === "" || clean.length % 2 !== 0 || !HEX_RE.test(clean)) return null;

  const buffer = new ArrayBuffer(clean.length / 2);
  const bytes = new Uint8Array(buffer);

  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }

  return buffer;
}

/** Déchiffre une réponse de l'API. Rend "" sur n'importe quel écart — corps
 *  vide, message d'erreur en clair, clé changée par l'opérateur. */
export async function decrypt(payload: string): Promise<string> {
  const cipher = hexToBuffer(payload);
  if (!cipher) return "";

  try {
    const key = await crypto.subtle.importKey(
      "raw",
      asciiBuffer(KEY),
      { name: "AES-CBC" },
      false,
      ["decrypt"],
    );

    const clear = await crypto.subtle.decrypt(
      { name: "AES-CBC", iv: asciiBuffer(IV) },
      key,
      cipher,
    );

    return new TextDecoder().decode(clear);
  } catch {
    // remplissage invalide : la clé n'est plus la bonne
    return "";
  }
}

export async function decryptVideo(payload: string): Promise<Embed4meVideo | null> {
  const clear = await decrypt(payload);
  if (clear === "") return null;

  try {
    return JSON.parse(clear) as Embed4meVideo;
  } catch {
    return null;
  }
}
